import log from "loglevel";

/*
 * ChatGPT Ads click id.
 *
 * When someone clicks one of our ads in ChatGPT, OpenAI appends
 * `?oppref=<opaque token>` to the landing URL. That token is the only thing that
 * ties a later purchase back to the ad, so we keep it from the landing page
 * until checkout, where it rides on the createSubscription request. The backend
 * stores it and reports the first paid invoice to OpenAI's Conversions API
 * (cca repo: documentation/51-chatgpt-ads-conversions.md).
 *
 * We do this by hand because we do not load OpenAI's browser pixel, which is
 * the thing that would otherwise capture it. The behavior below copies what the
 * pixel does with its `__oppref` cookie: keep the token for 30 days, and start
 * the 30 days again every time a landing URL carries one
 * (https://developers.openai.com/ads/measurement-pixel).
 *
 * Its own localStorage key, not a field on `sessionContext`: the session object
 * is replaced on login and logout, and an ad click usually happens before
 * either.
 */
export const OPPREF_STORAGE_KEY = "chatgptAds.oppref";
export const OPPREF_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// The backend drops anything longer (cca/payments.py, _OPPREF_MAX_LENGTH), so
// storing it would only carry a value that can never be used.
const OPPREF_MAX_LENGTH = 4096;

const storage = () => {
  // Guarded on `window` because Gatsby also evaluates modules during the SSR
  // build, where there is no localStorage.
  if (typeof window === "undefined") {
    return null;
  }
  try {
    // Reading the property can itself throw: a browser that blocks storage
    // (Safari with cookies blocked, a sandboxed iframe) raises SecurityError
    // from the `localStorage` getter, before any getItem/setItem. captureOppref
    // runs in gatsby-browser's onClientEntry, so letting that escape would stop
    // the rest of page start-up for those visitors (cca-app#825 review).
    return window.localStorage || null;
  } catch (err) {
    log.debug("chatgptAdsAttribution: localStorage unavailable", err);
    return null;
  }
};

/**
 * Store the click id from a landing URL's query string. Call once per page
 * load. A URL without `oppref` leaves a stored token untouched: the customer
 * usually browses, signs up and comes back before buying.
 */
export const captureOppref = (search, now = Date.now()) => {
  const store = storage();
  if (!store) {
    return;
  }
  const oppref = new URLSearchParams(search || "").get("oppref");
  if (!oppref || oppref.length > OPPREF_MAX_LENGTH) {
    return;
  }
  try {
    store.setItem(
      OPPREF_STORAGE_KEY,
      JSON.stringify({ value: oppref, capturedAt: now }),
    );
  } catch (err) {
    // Private mode / storage disabled. Losing the attribution is the whole
    // cost; the page must still load.
    log.debug("chatgptAdsAttribution: localStorage unwritable", err);
  }
};

/**
 * The stored click id, or null when there is none or it is older than 30 days.
 * An expired or unreadable entry is removed so it is not parsed again.
 */
export const getStoredOppref = (now = Date.now()) => {
  const store = storage();
  if (!store) {
    return null;
  }
  try {
    const raw = store.getItem(OPPREF_STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const { value, capturedAt } = JSON.parse(raw);
    if (
      typeof value !== "string" ||
      typeof capturedAt !== "number" ||
      now - capturedAt > OPPREF_MAX_AGE_MS
    ) {
      store.removeItem(OPPREF_STORAGE_KEY);
      return null;
    }
    return value;
  } catch (err) {
    // Unreadable storage, or an entry that is not our JSON. Checkout must not
    // fail over an ad-tracking value.
    log.debug("chatgptAdsAttribution: localStorage unreadable", err);
    return null;
  }
};
